import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Crown, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'jsf_ambassador_cta_dismissed';
// Hide on these routes (admin, account, ambassador internal pages, checkout)
const HIDDEN_PREFIXES = ['/admin', '/ambassador-apply', '/ambassador', '/checkout', '/cart', '/account', '/login', '/register'];

/**
 * Subtle dismissible floating CTA inviting visitors to apply as an ambassador.
 * - Bottom-right, gold accent, glass look.
 * - Hidden if user is already an ambassador, on admin/account/checkout pages, or dismissed this session.
 * - Appears 6 seconds after first paint to avoid distracting from initial scan.
 */
export default function FloatingAmbassadorCTA() {
  const { user } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);

  const isAmbassador = user && (user.role === 'ambassador' || user.is_ambassador);
  const onHiddenRoute = HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p));
  const dismissed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STORAGE_KEY) === '1';

  useEffect(() => {
    if (isAmbassador || onHiddenRoute || dismissed) { setShow(false); return; }
    const t = setTimeout(() => setShow(true), 6000);
    return () => clearTimeout(t);
  }, [isAmbassador, onHiddenRoute, dismissed, location.pathname]);

  const dismiss = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[55] max-w-[320px] sm:max-w-[360px] animate-fade-in-up"
      data-testid="floating-ambassador-cta"
      role="complementary"
      aria-label="Become an ambassador invitation"
    >
      <Link
        to="/ambassador-apply"
        className="group block bg-[#0E0E0E]/95 backdrop-blur-md border border-[#D4AF37]/30 hover:border-[#D4AF37] shadow-2xl pl-4 pr-10 py-3.5 transition-all hover:-translate-y-0.5"
        data-testid="floating-ambassador-link"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans font-medium">Earn with us</p>
            <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors truncate">
              Become a JS Ambassador →
            </p>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss ambassador invitation"
        className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white transition-colors"
        data-testid="floating-ambassador-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
